import React, { useState, useEffect } from 'react';
import { saasData } from '../../lib/supabase';
import correlationService from '../../lib/services/correlationService';
import TeacherService from '../../lib/services/teacherService';
import GroupService from '../../lib/services/groupService';
import SubjectService from '../../lib/services/subjectService';
import SubjectManagementModal from './SubjectManagementModal';
import CommitteeManagementModal from './CommitteeManagementModal';
import downloadService from '../../lib/services/downloadService';
import './TeacherInfoPage.css';

const TeacherInfoPage = ({ onBack, onTabChange }) => {
  const [teacherData, setTeacherData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('overview'); // 'overview', 'class-master', 'subjects', 'groups'
  const [selectedTeacher, setSelectedTeacher] = useState(null);
  const [teacherClassInfo, setTeacherClassInfo] = useState(null);
  const [groupData, setGroupData] = useState(null);
  const [subjectData, setSubjectData] = useState(null);
  const [subjectModalOpen, setSubjectModalOpen] = useState(false);
  const [committeeModalOpen, setCommitteeModalOpen] = useState(false);
  const [selectedSubject, setSelectedSubject] = useState(null);
  const [selectedCommittee, setSelectedCommittee] = useState(null);
  const [teacherService] = useState(new TeacherService('tn_01'));
  const [groupService] = useState(new GroupService());
  const [subjectService] = useState(new SubjectService());

  useEffect(() => {
    fetchTeacherData();
    fetchGroupData();
    fetchSubjectData();
    // Register navigation callback
    correlationService.registerNavigationCallback('teacher', handleTeacherNavigation);
  }, []);

  const handleTeacherNavigation = (context) => {
    if (context.teacherId) {
      setSelectedTeacher(context.teacherId);
      loadTeacherClassInfo(context.teacherId);
    }
  };

  const loadTeacherClassInfo = async (teacherId) => {
    try {
      console.log('Loading class info for teacher:', teacherId);
      const { data: classInfo, error } = await teacherService.getClassMasterInfo(teacherId);
      if (error) {
        console.error('Error loading teacher class info:', error);
        // Provide fallback sample data for demonstration
        const fallbackData = {
          teacher: { name: '丁老師', teacher_id: teacherId },
          classes: [
            {
              class_id: 'J1B',
              name: '中一乙班',
              className: '中一乙班',
              class_code: 'J1B',
              form_level: 'Junior Form 1',
              studentCount: 25,
              statistics: {
                homeworkCount: 2,
                attendanceRate: 95,
                averageGrade: 'B+',
                totalStudents: 25
              }
            },
            {
              class_id: 'J2C',
              name: '中二丙班',
              className: '中二丙班',
              class_code: 'J2C',
              form_level: 'Junior Form 2',
              studentCount: 23,
              statistics: {
                homeworkCount: 3,
                attendanceRate: 92,
                averageGrade: 'A-',
                totalStudents: 23
              }
            }
          ],
          homework: [],
          attendance: [],
          academic: { average: 'B+', totalStudents: 48, passingRate: 93 },
          slp: [],
          summary: { totalClasses: 2, totalStudents: 48, totalHomework: 5, overallAttendanceRate: 93 }
        };
        setTeacherClassInfo(fallbackData);
        setActiveTab('class-master');
        return;
      }
      console.log('Class info loaded:', classInfo);
      setTeacherClassInfo(classInfo);
      setActiveTab('class-master');
    } catch (error) {
      console.error('Error loading teacher class info:', error);
      setTeacherClassInfo(null);
    }
  };

  const handleTeacherClick = async (teacher) => {
    setSelectedTeacher(teacher);
    await loadTeacherClassInfo(teacher.teacher_id || teacher.id);
  };

  const navigateToClass = (classId) => {
    if (onTabChange) {
      onTabChange('class-info', { classId });
    }
  };


  const navigateToStudent = (studentId) => {
    if (onTabChange) {
      onTabChange('student-info', { studentId });
    }
  };

  const fetchTeacherData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      console.log('Starting to fetch teacher data...');
      
      // Fetch teacher data using the new service
      const { data, error: fetchError } = await teacherService.getTeacherData();
      
      console.log('Teacher service response:', { data, error: fetchError });
      
      if (fetchError) {
        console.error('Error fetching teacher data:', fetchError);
        setError(fetchError);
        return;
      }
      
      if (!data) {
        console.error('No data returned from teacher service');
        setError('No data returned from database');
        return;
      }
      
      setTeacherData(data);
      console.log('Teacher data loaded successfully:', data);
      
    } catch (err) {
      console.error('Unexpected error in fetchTeacherData:', err);
      setError(err.message || 'Failed to fetch teacher data');
    } finally {
      setLoading(false);
    }
  };

  const fetchGroupData = async () => {
    try {
      const { groups, error } = await groupService.getGroupMemberships();
      if (error) {
        console.error('Error fetching group data:', error);
      } else {
        setGroupData(groups);
      }
    } catch (err) {
      console.error('Error in fetchGroupData:', err);
    }
  };

  const fetchSubjectData = async () => {
    try {
      const { subjects, error } = await subjectService.getSubjectTeacherAssignments();
      if (error) {
        console.error('Error fetching subject data:', error);
      } else {
        setSubjectData(subjects);
      }
    } catch (err) {
      console.error('Error in fetchSubjectData:', err);
    }
  };

  // Management functions
  const handleManageSubject = (subject) => {
    setSelectedSubject(subject);
    setSubjectModalOpen(true);
  };

  const handleManageCommittee = (committee) => {
    setSelectedCommittee(committee);
    setCommitteeModalOpen(true);
  };

  const handleSubjectUpdate = () => {
    fetchSubjectData();
  };

  const handleCommitteeUpdate = () => {
    fetchGroupData();
  };

  // Download functions
  const handleDownloadTeacherList = async () => {
    try {
      console.log('=== SIMPLE TEACHER DOWNLOAD ===');
      
      // Fetch simple teacher data directly from database, ordered by teacher_no
      const { data: teachers, error } = await saasData.supabase
        .from('saas_teachers')
        .select('name, subject_specialty, teacher_no')
        .eq('tenant_id', 'eadcd263-a399-4261-8cbd-8fb1ce6da0e3')
        .order('teacher_no', { ascending: true });
      
      if (error) {
        console.error('Error fetching teachers:', error);
        alert('無法獲取教師資料: ' + error.message);
        return;
      }
      
      if (!teachers || teachers.length === 0) {
        alert('沒有教師資料可供下載');
        return;
      }
      
      console.log('Fetched teachers:', teachers.length);
      console.log('Sample teacher:', teachers[0]);
      
      // Create simple data structure
      const downloadData = {
        teachers: teachers
      };
      
      await downloadService.downloadTeacherList(downloadData, '教師名單.pdf');
      alert('教師名單下載成功！');
      
    } catch (error) {
      console.error('Error downloading teacher list:', error);
      alert('下載失敗: ' + error.message);
    }
  };

  const handleDownloadSubjectTeachers = async () => {
    try {
      console.log('Subject data for download:', subjectData);
      await downloadService.downloadSubjectTeachers(subjectData || [], '科目教師名單.pdf');
    } catch (error) {
      console.error('Error downloading subject teachers:', error);
      alert('下載失敗: ' + error.message);
    }
  };

  const handleDownloadCommitteeMembers = async () => {
    try {
      console.log('Group data for download:', groupData);
      await downloadService.downloadCommitteeMembers(groupData || {}, '組別成員名單.pdf');
    } catch (error) {
      console.error('Error downloading committee members:', error);
      alert('下載失敗: ' + error.message);
    }
  };

  // Test download function
  const handleTestDownload = async () => {
    try {
      console.log('Testing download functionality...');
      await downloadService.testDownload();
      alert('測試下載成功！');
    } catch (error) {
      console.error('Test download failed:', error);
      alert('測試下載失敗: ' + error.message);
    }
  };

  // Test download with hardcoded data
  const handleTestHardcodedDownload = async () => {
    try {
      console.log('Testing download with hardcoded data...');
      await downloadService.testDownloadWithHardcodedData();
      alert('硬編碼測試下載成功！');
    } catch (error) {
      console.error('Hardcoded test download failed:', error);
      alert('硬編碼測試下載失敗: ' + error.message);
    }
  };

  // Debug data structure
  const handleDebugData = () => {
    console.log('=== DEBUG DATA STRUCTURE ===');
    console.log('teacherData:', teacherData);
    console.log('teacherData type:', typeof teacherData);
    console.log('teacherData keys:', teacherData ? Object.keys(teacherData) : 'null');
    
    if (teacherData) {
      console.log('totalTeachers:', teacherData.totalTeachers);
      console.log('uniqueSubjects:', teacherData.uniqueSubjects);
      console.log('teachers array:', teacherData.teachers);
      console.log('teachers length:', teacherData.teachers?.length);
      
      if (teacherData.teachers && teacherData.teachers.length > 0) {
        console.log('First teacher:', teacherData.teachers[0]);
        console.log('First teacher keys:', Object.keys(teacherData.teachers[0]));
      }
    }
    
    alert('數據結構已輸出到控制台，請查看瀏覽器開發者工具');
  };

  const getTeacherAvatar = (name) => {
    if (!name) return '?';
    return name.charAt(0);
  };

  const getTeacherTitle = (teacher) => {
    if (teacher.position) return teacher.position;
    if (teacher.title) return teacher.title;
    if (teacher.subject) return `${teacher.subject}科主任`;
    if (teacher.role) return teacher.role;
    return '教師';
  };

  const getTeacherSubject = (teacher) => {
    if (teacher.subject) return teacher.subject;
    if (teacher.subjects && Array.isArray(teacher.subjects)) {
      return teacher.subjects[0] || '未指定';
    }
    if (teacher.department) return teacher.department;
    if (teacher.major) return teacher.major;
    return '未指定';
  };

  // Add fallback data if no teachers found
  const getFallbackData = () => {
    return {
      totalTeachers: 0,
      uniqueSubjects: 0,
      seniorTeachers: 0,
      teachers: [],
      departments: {
        '語文科': { name: '語文科', subjects: '中文、英文、普通話', teacherCount: 0 },
        '數理科': { name: '數理科', subjects: '數學、科學、電腦', teacherCount: 0 },
        '人文科': { name: '人文科', subjects: '歷史、地理、公民', teacherCount: 0 },
        '藝術科': { name: '藝術科', subjects: '音樂、美術、體育', teacherCount: 0 }
      }
    };
  };

  // Validate and process teacher data
  const validateTeacherData = (data) => {
    if (!data || typeof data !== 'object') {
      console.warn('Invalid teacher data structure:', data);
      return getFallbackData();
    }

    // Ensure required fields exist
    const validatedData = {
      totalTeachers: data.totalTeachers || 0,
      uniqueSubjects: data.uniqueSubjects || 0,
      seniorTeachers: data.seniorTeachers || 0,
      teachers: Array.isArray(data.teachers) ? data.teachers : [],
      departments: data.departments || getFallbackData().departments
    };

    console.log('Validated teacher data:', validatedData);
    return validatedData;
  };

  if (loading) {
    return (
      <div className="teacher-info-page">
        <div className="page-header">
          <button className="back-button" onClick={onBack}>
            ← 返回主頁
          </button>
          <h1>教師資訊</h1>
        </div>
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>正在載入教師資料...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="teacher-info-page">
        <div className="page-header">
          <button className="back-button" onClick={onBack}>
            ← 返回主頁
          </button>
          <h1>教師資訊</h1>
        </div>
        <div className="error-container">
          <p>載入失敗: {error}</p>
          <button onClick={fetchTeacherData} className="retry-button">
            重試
          </button>
        </div>
      </div>
    );
  }

  if (!teacherData) {
    return (
      <div className="teacher-info-page">
        <div className="page-header">
          <button className="back-button" onClick={onBack}>
            ← 返回主頁
          </button>
          <h1>教師資訊</h1>
        </div>
        <div className="no-data-container">
          <p>暫無教師資料</p>
        </div>
      </div>
    );
  }

  return (
    <div className="teacher-info-page">
      {/* Header */}
      <div className="page-header">
        <button className="back-button" onClick={onBack}>
          ← 返回主頁
        </button>
        <h1>教師資訊</h1>
        <button className="refresh-button" onClick={fetchTeacherData} title="重新載入">
          🔄
        </button>
      </div>

      {/* Navigation Tabs */}
      <div className="teacher-tabs">
        <button 
          className={`tab-btn ${activeTab === 'overview' ? 'active' : ''}`}
          onClick={() => setActiveTab('overview')}
        >
          教師概覽
        </button>
        <button 
          className={`tab-btn ${activeTab === 'class-master' ? 'active' : ''}`}
          onClick={() => setActiveTab('class-master')}
        >
          班主任功能
        </button>
        <button 
          className={`tab-btn ${activeTab === 'subjects' ? 'active' : ''}`}
          onClick={() => setActiveTab('subjects')}
        >
          科目管理
        </button>
        <button 
          className={`tab-btn ${activeTab === 'groups' ? 'active' : ''}`}
          onClick={() => setActiveTab('groups')}
        >
          組別Category
        </button>
      </div>

      {/* Content */}
      <div className="page-content">
        {activeTab === 'overview' && (
          <>
        {/* Summary Cards */}
        <div className="summary-section">
          <div className="section-header">
            <h2>教師概覽</h2>
            <div className="download-buttons">
              <button 
                className="download-button"
                onClick={handleDownloadTeacherList}
                title="下載教師名單"
              >
                <span className="download-icon">■</span>
                下載教師名單
              </button>
              <button 
                className="download-button test-button"
                onClick={handleTestDownload}
                title="測試下載功能"
              >
                <span className="download-icon">🧪</span>
                測試下載
              </button>
              <button 
                className="download-button test-button"
                onClick={handleTestHardcodedDownload}
                title="測試硬編碼數據下載"
              >
                <span className="download-icon">🔧</span>
                硬編碼測試
              </button>
              <button 
                className="download-button test-button"
                onClick={handleDebugData}
                title="調試數據結構"
              >
                <span className="download-icon">🐛</span>
                調試數據
              </button>
            </div>
          </div>
          <div className="summary-card">
            <div className="card-icon">👨‍🏫</div>
            <div className="card-content">
              <h3>總教師數</h3>
              <div className="card-number">{teacherData.totalTeachers}</div>
              <p>全職教師</p>
            </div>
          </div>
          
          <div className="summary-card">
            <div className="card-icon">📚</div>
            <div className="card-content">
              <h3>教學科目</h3>
              <div className="card-number">{teacherData.uniqueSubjects}</div>
              <p>主要學科</p>
            </div>
          </div>
          
          <div className="summary-card">
            <div className="card-icon">🏆</div>
            <div className="card-content">
              <h3>資深教師</h3>
              <div className="card-number">{teacherData.seniorTeachers}</div>
              <p>5年以上經驗</p>
            </div>
          </div>
        </div>

        {/* Teacher List */}
        <div className="teacher-list-section">
          <h2>教師名單</h2>
          <div className="teacher-grid">
            {teacherData.teachers && teacherData.teachers.length > 0 ? (
              teacherData.teachers.map((teacher, index) => (
                <div 
                  key={teacher.id || index} 
                  className="teacher-item clickable"
                  onClick={() => handleTeacherClick(teacher)}
                >
                  <div className="teacher-avatar">
                    {getTeacherAvatar(teacher.name)}
                  </div>
                  <div className="teacher-info">
                    <h4>{teacher.name || `教師${index + 1}`}</h4>
                    <p>{getTeacherTitle(teacher)}</p>
                    <span className="teacher-subject">
                      {getTeacherSubject(teacher)}
                    </span>
                  </div>
                  <div className="teacher-actions">
                    <button 
                      className="action-btn"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleTeacherClick(teacher);
                      }}
                    >
                      查看詳情
                    </button>
                  </div>
                </div>
              ))
            ) : (
              <div className="no-teachers">
                <p>暫無教師資料</p>
              </div>
            )}
          </div>
        </div>

        {/* Department Overview */}
        <div className="department-section">
          <h2>學科部門</h2>
          <div className="department-grid">
            {Object.values(teacherData.departments).map((dept, index) => (
              <div key={index} className="department-card">
                <h3>{dept.name}</h3>
                <p>{dept.subjects}</p>
                <div className="teacher-count">
                  {dept.teacherCount} 位教師
                </div>
              </div>
            ))}
          </div>
        </div>
          </>
        )}

        {activeTab === 'class-master' && selectedTeacher && (
          <div className="class-master-section">
            <h2>班主任功能 - {selectedTeacher.name}</h2>
            {teacherClassInfo ? (
              <div className="class-master-content">
                {/* Class Information */}
                <div className="class-info-card">
                  <h3>班級資訊</h3>
                  <div className="class-details">
                    {teacherClassInfo.classes && teacherClassInfo.classes.length > 0 ? (
                      teacherClassInfo.classes.map((cls, index) => (
                        <div key={index} className="class-item-3layers">
                          {/* TOP LAYER - Class Info + 3 Stats Cards */}
                          <div className="top-layer">
                            <div className="class-label">
                              <div className="class-name">{cls.name || cls.className || cls.class_code}</div>
                              <div className="student-count">{cls.students?.length || 0} 位學生</div>
                            </div>
                            <div className="stats-cards-horizontal">
                              <div className="stat-card">
                                <div className="stat-icon">📚</div>
                                <div className="stat-content">
                                  <span className="stat-value">{cls.homework?.length || 0}</span>
                                  <span className="stat-label">家課數量</span>
                                </div>
                              </div>
                              <div className="stat-card">
                                <div className="stat-icon">📊</div>
                                <div className="stat-content">
                                  <span className="stat-value">{cls.attendance?.rate || 0}%</span>
                                  <span className="stat-label">出席率</span>
                                </div>
                              </div>
                              <div className="stat-card">
                                <div className="stat-icon">🎯</div>
                                <div className="stat-content">
                                  <span className="stat-value">{cls.academic?.avgGrade || 'N/A'}</span>
                                  <span className="stat-label">平均成績</span>
                                </div>
                              </div>
                            </div>
                          </div>
                          
                          {/* CENTER LAYER - Students Grid */}
                          <div className="center-layer">
                            <h4>學生名單 ({cls.students?.length || 0} 人)</h4>
                            <div className="students-grid-4col">
                              {cls.students && cls.students.length > 0 ? cls.students.map((student, studentIndex) => (
                                <div key={studentIndex} className="student-card">
                                  <div className="student-avatar">
                                    {student.name.charAt(0)}
                                  </div>
                                  <div className="student-info">
                                    <div className="student-id">{student.student_id}</div>
                                    <div className="student-name">{student.name}</div>
                                    <div className="student-email">{student.email}</div>
                                  </div>
                                  <div className="student-actions">
                                    <button 
                                      className="view-student-btn"
                                      onClick={() => navigateToStudent(student.student_id)}
                                      title="查看學生詳情"
                                    >
                                      👁️
                                    </button>
                                  </div>
                                </div>
                              )) : (
                                <div className="no-students">
                                  <p>此班級暫無學生資料</p>
                                </div>
                              )}
                            </div>
                          </div>
                          
                          {/* BOTTOM LAYER - Action Buttons */}
                          <div className="bottom-layer">
                            <button 
                              className="action-btn primary"
                              onClick={() => navigateToClass(cls.class_id || cls.classId || cls.class_code)}
                            >
                              查看班級詳情
                            </button>
                            <button 
                              className="action-btn"
                              onClick={() => onTabChange('homework', { classId: cls.class_id || cls.classId || cls.class_code })}
                            >
                              家課管理
                            </button>
                            <button 
                              className="action-btn"
                              onClick={() => onTabChange('attendance', { classId: cls.class_id || cls.classId || cls.class_code })}
                            >
                              考勤管理
                            </button>
                          </div>
                        </div>
                      ))
                    ) : (
                      <div className="no-classes">
                        <p>此教師暫無班級分配</p>
                        <p className="class-info-note">
                          {selectedTeacher?.isClassMaster ? '此教師為班主任，但尚未分配班級' : '此教師為科目教師'}
                        </p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Quick Access to Related Modules */}
                <div className="quick-access-section">
                  <h3>快速存取</h3>
                  <div className="quick-access-grid">
                    <div className="quick-access-item">
                      <h4>時間表</h4>
                      <p>查看班級時間表</p>
                      <button 
                        className="quick-btn"
                        onClick={() => onTabChange('homework', { classId: teacherClassInfo.classes[0]?.classId, view: 'timetable' })}
                      >
                        查看時間表
                      </button>
                    </div>
                    <div className="quick-access-item">
                      <h4>家課管理</h4>
                      <p>管理班級家課</p>
                      <button 
                        className="quick-btn"
                        onClick={() => onTabChange('homework', { classId: teacherClassInfo.classes[0]?.classId })}
                      >
                        家課管理
                      </button>
                    </div>
                    <div className="quick-access-item">
                      <h4>考勤記錄</h4>
                      <p>查看學生考勤</p>
                      <button 
                        className="quick-btn"
                        onClick={() => onTabChange('attendance', { classId: teacherClassInfo.classes[0]?.classId })}
                      >
                        考勤記錄
                      </button>
                    </div>
                    <div className="quick-access-item">
                      <h4>學業成績</h4>
                      <p>查看學生成績</p>
                      <button 
                        className="quick-btn"
                        onClick={() => onTabChange('grades', { classId: teacherClassInfo.classes[0]?.classId })}
                      >
                        學業成績
                      </button>
                    </div>
                    <div className="quick-access-item">
                      <h4>學習歷程</h4>
                      <p>查看SLP記錄</p>
                      <button 
                        className="quick-btn"
                        onClick={() => onTabChange('slp', { classId: teacherClassInfo.classes[0]?.classId })}
                      >
                        SLP管理
                      </button>
                    </div>
                    <div className="quick-access-item">
                      <h4>學生資訊</h4>
                      <p>查看學生詳情</p>
                      <button 
                        className="quick-btn"
                        onClick={() => onTabChange('student-info', { classId: teacherClassInfo.classes[0]?.classId })}
                      >
                        學生資訊
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div className="loading-class-info">
                <p>載入班級資訊中...</p>
              </div>
            )}
          </div>
        )}

        {activeTab === 'subjects' && (
          <div className="subjects-section">
            <div className="section-header">
              <h2>科目管理</h2>
              <button 
                className="download-button"
                onClick={handleDownloadSubjectTeachers}
                title="下載科目教師名單"
              >
                <span className="download-icon">■</span>
                下載科目教師名單
              </button>
            </div>
            {subjectData && subjectData.length > 0 ? (
              <div className="subjects-container">
                {subjectData.map((subject, index) => (
                  <div key={index} className="subject-section">
                    <div className="subject-header">
                      <h3 className="subject-title">{subject.name}</h3>
                      <div className="subject-actions">
                        <span className="teacher-count">({subject.teachers.length} 位教師)</span>
                        <button 
                          className="manage-button"
                          onClick={() => handleManageSubject(subject)}
                          title="管理教師"
                        >
                          ⚙️ 管理
                        </button>
                      </div>
                    </div>
                    <div className="teachers-grid">
                      {subject.teachers.map((teacher, teacherIndex) => (
                        <div key={teacherIndex} className="teacher-card">
                          <div className="teacher-avatar">
                            {getTeacherAvatar(teacher.name)}
                          </div>
                          <div className="teacher-info">
                            <div className="teacher-name">{teacher.name}</div>
                            <div className="teacher-specialty">{teacher.subject_specialty || '未指定'}</div>
                            {teacher.role && (
                              <div className="teacher-role">
                                <span className="role-badge">{teacher.role}</span>
                              </div>
                            )}
                          </div>
                          <div className="teacher-actions">
                            <button
                              className="action-btn"
                              onClick={() => handleTeacherClick(teacher)}
                            >
                              查看詳情
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="no-subjects">
                <p>載入科目資料中...</p>
              </div>
            )}
          </div>
        )}

        {activeTab === 'groups' && (
          <div className="groups-section">
            <div className="section-header">
              <h2>組別Category</h2>
              <button 
                className="download-button"
                onClick={handleDownloadCommitteeMembers}
                title="下載組別成員名單"
              >
                <span className="download-icon">■</span>
                下載組別成員名單
              </button>
            </div>
            {groupData ? (
              <div className="groups-container">
                {Object.entries(groupData).map(([categoryKey, category]) => (
                  <div key={categoryKey} className="group-category">
                    <h3 className="category-title">{category.name}</h3>
                    <div className="groups-grid">
                      {category.groups.map((group) => (
                        <div key={group.id} className="group-card">
                          <div className="group-header">
                            <h4 className="group-name">{group.chineseName}</h4>
                            <div className="group-header-right">
                              <span className="group-english">{group.name}</span>
                              <button 
                                className="manage-button"
                                onClick={() => handleManageCommittee(group)}
                                title="管理成員"
                              >
                                ⚙️ 管理
                              </button>
                            </div>
                          </div>
                          <div className="group-members">
                            <h5>組別成員 ({group.members?.length || 0} 人)</h5>
                            <div className="members-grid">
                              {group.members && group.members.length > 0 ? (
                                group.members.map((teacher, index) => (
                                  <div key={index} className="member-card">
                                    <div className="member-avatar">
                                      {getTeacherAvatar(teacher.name)}
                                    </div>
                                    <div className="member-info">
                                      <div className="member-name">{teacher.name}</div>
                                      <div className="member-subject">{teacher.subject_specialty || '未指定'}</div>
                                      {teacher.role && (
                                        <div className="member-role">
                                          <span className="role-badge">{teacher.role}</span>
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                ))
                              ) : (
                                <div className="no-members">
                                  <p>暫無成員</p>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="loading-groups">
                <p>載入組別資料中...</p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Management Modals */}
      <SubjectManagementModal
        isOpen={subjectModalOpen}
        onClose={() => setSubjectModalOpen(false)}
        subjectName={selectedSubject?.name}
        currentTeachers={selectedSubject?.teachers || []}
        onUpdate={handleSubjectUpdate}
      />

      <CommitteeManagementModal
        isOpen={committeeModalOpen}
        onClose={() => setCommitteeModalOpen(false)}
        committeeName={selectedCommittee?.chineseName}
        currentMembers={selectedCommittee?.members || []}
        onUpdate={handleCommitteeUpdate}
      />
    </div>
  );
};

export default TeacherInfoPage;
